import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { SecretIdentityProviderProps, SecretIdentityContext } from './Types'
import html2canvas from 'html2canvas'
import { identities } from 'components/SecretIdentityModal/identities'
import { useAuth } from 'hooks/UseAuth'

const Context = createContext<Partial<SecretIdentityContext>>({})

export const Provider = ({ children, secretIdentity }: PropsWithChildren<SecretIdentityProviderProps>) => {
  const selectedIdentity = identities[secretIdentity.identity_key]
  const cardRef = useRef<HTMLDivElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [assetsLoaded, setAssetsLoaded] = useState(false)
  const auth = useAuth()

  useEffect(() => {
    if (!selectedIdentity) {
      setFile(null)
      setAssetsLoaded(false)
    }
  }, [selectedIdentity])

  useEffect(() => {
    if (!selectedIdentity) return
    setFile(null)
    setAssetsLoaded(false)

    const card = cardRef.current
    if (!card) return

    const images = card.querySelectorAll('img')

    if (images.length === 0) {
      setAssetsLoaded(true) // If no images, consider assets loaded
      return
    }

    let loadedImagesCount = 0

    images.forEach((img) => {
      if (img.complete) {
        loadedImagesCount++ // If image is already loaded
      } else {
        img.addEventListener('load', () => {
          loadedImagesCount++
          if (loadedImagesCount === images.length) {
            setAssetsLoaded(true)
          }
        })
      }
    })

    if (loadedImagesCount === images.length) {
      setAssetsLoaded(true)
    }
  }, [selectedIdentity])

  // Convert the card to a blob when it's rendered
  useEffect(() => {
    if (!assetsLoaded) return

    const card = cardRef.current
    if (!card) return

    html2canvas(card, { allowTaint: true, useCORS: true }).then((canvas) => {
      canvas.toBlob(function (blob) {
        if (!blob) return

        const file = new File([blob], 'identity.png', { type: 'image/png' })
        setFile(file)
      })
    })
  }, [assetsLoaded, selectedIdentity])

  return (
    <Context.Provider
      value={{
        file,
        secretIdentity,
        selectedIdentity,
        cardRef,
        displayName: auth?.first_name || undefined,
        startDate: auth?.created_at,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useSecretIdentity = () => useContext(Context)
